if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest('.js-product');
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');

      // Bind to on:variant:change event for this product
      this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Init
      this.updateInventory(
        this.dataset.stockBand,
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        this.updateInventory(
          inventory.stock_band,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory('zero', false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {string} band - stock band from Liquid (the exact count never reaches the browser)
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(band, available, inventoryPolicy) {
      if (band === 'zero' && !available && this.hidden) {
        return;
      }

      if (this.dataset.preorder === 'true' && available) {
        this.hidden = false;
        this.setAttribute('data-inventory-level', 'preorder');
        this.inventoryNotice.innerText = this.dataset.textPreorder;
        if (this.urgencyMessage) this.urgencyMessage.innerHTML = '';
        if (this.indicatorBar) this.indicatorBar.hidden = true;
        return;
      }

      const bands = {
        many: { level: 'normal', text: theme.strings.stockMany, width: 100 },
        under10: { level: 'low', text: theme.strings.stockUnder10, width: 60 },
        few: { level: 'low', text: theme.strings.stockFew, width: 35 },
        two: { level: 'critical', text: theme.strings.stockTwo, width: 15 },
        one: { level: 'critical', text: theme.strings.stockOne, width: 8 }
      };
      const stock = bands[band];
      const isLow = stock && (stock.level === 'low' || stock.level === 'critical');
      let inventoryLevel;

      if (!stock) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else {
        inventoryLevel = stock.level;
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && isLow))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Localized band labels only (BG-165).
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = theme.strings.backordered;
        } else if (stock) {
          this.inventoryNotice.innerText = stock.text;
        } else if (inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (isLow) {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          // Fixed widths by band, never proportional to the private quantity.
          const newWidth = stock ? stock.width : (inventoryLevel === 'in_stock' ? 100 : 0);
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
